import { Spin } from 'antd';
import { useLayoutEffect, useRef } from 'react';

import { type BadgeLibrary, useBadgeLibrary } from './BadgeLibrary';

/**
 * Draws a static display object from the PlayerBadge library onto its own
 * canvas. Renders once per change of `deps`; nothing is added to the Ticker.
 */
export function BadgeCanvas({
  width,
  height,
  displayWidth = width,
  draw,
  deps,
}: {
  /** Native (Animate) width of the drawing area. */
  width: number;
  /** Native (Animate) height of the drawing area. */
  height: number;
  /** Rendered CSS width; height follows the native aspect ratio. */
  displayWidth?: number;
  draw: (lib: BadgeLibrary) => createjs.DisplayObject;
  deps: unknown[];
}) {
  const lib = useBadgeLibrary();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const displayHeight = (displayWidth * height) / width;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!lib || !canvas) return;

    const scale = (window.devicePixelRatio || 1) * (displayWidth / width);
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const stage = new createjs.Stage(canvas);
    stage.scaleX = stage.scaleY = scale;
    stage.addChild(draw(lib));
    stage.update();

    return () => {
      stage.removeAllChildren();
      stage.clear();
    };
  }, [lib, width, height, displayWidth, ...deps]);

  const size = { width: displayWidth, height: displayHeight };

  return lib ? (
    <canvas ref={canvasRef} style={{ ...size, display: 'block' }} />
  ) : (
    <Spin style={size} />
  );
}
